import { useCallback } from 'react';

import { useMostPopularBetsProps } from '../context/MostPopularBetsContext';
import { trackEvent } from '../utils/trackEvent';

export type MostPopularBetsEvent =
  'most_popular_bets_odds_click' | 'most_popular_bets_selection_click' | 'most_popular_bets_filter_change';

/**
 * Centralises the module's GA wiring: respects `disableAnalytics`, spreads the host-provided
 * impression context into every payload, and targets the configured dataLayer (host-supplied
 * or `window.dataLayer`). Returns `track` for click/interaction events plus the impression
 * config consumed by `useTrackImpression`.
 */
export const useAnalytics = () => {
  const { analyticsDataLayer, analyticsImpressionEventData, analyticsImpressionEventName, disableAnalytics } =
    useMostPopularBetsProps();

  const track = useCallback(
    (event: MostPopularBetsEvent, data: Record<string, unknown> = {}) => {
      if (disableAnalytics) return;
      // Spread the generic host impression context first so the specific per-click fields
      // (betId, sport, position, …) always win and can't be clobbered by host-supplied keys.
      trackEvent(event, { ...analyticsImpressionEventData, ...data }, analyticsDataLayer);
    },
    [analyticsDataLayer, analyticsImpressionEventData, disableAnalytics],
  );

  return {
    analyticsDataLayer,
    analyticsImpressionEventData,
    // Suppress impressions entirely when analytics is disabled.
    impressionEventName: disableAnalytics ? undefined : analyticsImpressionEventName,
    track,
  };
};
